import { execFile, spawn } from 'node:child_process';
import { createServer } from 'node:net';
import { promisify } from 'node:util';
import { getDockerCmd, runDockerForeground, runDockerStreaming } from '../utils/docker-cmd.js';
import { getLogger } from '../utils/logger.js';
import { warnIfEmulatedPlatform } from './docker-image-builder.js';
import { getEmbedConfig } from './embed-config.js';

const execFileAsync = promisify(execFile);

/**
 * Wraps `docker pull` / `docker run` / `docker rm` for `cdkl invoke`.
 *
 * Mirrors the style of `src/assets/docker-asset-publisher.ts` (execFile for
 * one-shot calls, spawn for long-running ones). Kept as a separate file so
 * the command layer's wiring stays small; PR 5 (container Lambda) is
 * expected to add a second non-build call site, at which point the
 * common surface can be lifted into a shared helper.
 */

export class DockerRunnerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DockerRunnerError';
    Object.setPrototypeOf(this, DockerRunnerError.prototype);
  }
}

export interface DockerRunOptions {
  /** Image to run (e.g. `public.ecr.aws/lambda/nodejs:20`). */
  image: string;
  /**
   * Bind mounts: `[hostPath, containerPath]` pairs. cdk-local uses this to
   * expose the function's local code at `/var/task` (read-only). Empty
   * for container Lambdas (PR 5) — the image already has the code at
   * `/var/task`, no host bind-mount needed.
   */
  mounts: { hostPath: string; containerPath: string; readOnly?: boolean }[];
  /**
   * Additional bind mounts applied AFTER `mounts` (PR 6 of #224, issue
   * #232 — Lambda Layers). The split is purely organizational: it lets
   * the call site keep "the function's own code" (`mounts`) separate
   * from any extra mounts the caller wants to compose. The docker-runner
   * emits one `-v <hostPath>:<containerPath>:<ro?>` per entry, in
   * order, with NO target-path coalescing — Docker rejects duplicate
   * targets (`Error response from daemon: Duplicate mount point: ...`),
   * so the caller MUST ensure each entry's `containerPath` is unique
   * across `mounts` + `extraMounts`. For Lambda Layers specifically:
   * AWS's "last layer wins on file collision" semantic is realized by
   * the caller (`materializeLambdaLayers` in `local-invoke.ts` /
   * `local-start-api.ts`) `cpSync`-merging every layer's asset
   * directory into ONE host tmpdir in template order, then passing a
   * single `{hostPath: <tmpdir>, containerPath: '/opt'}` entry here —
   * NOT one mount per layer.
   */
  extraMounts?: { hostPath: string; containerPath: string; readOnly?: boolean }[];
  /** Environment variables to forward into the container. */
  env: Record<string, string>;
  /**
   * Extra env keys (beyond {@link SENSITIVE_ENV_KEYS}) whose VALUES are
   * sensitive and must be routed through docker's value-from-process-env
   * form (`-e KEY`) so they never appear on the `docker run` argv. Today
   * this carries the env keys that resolved to a decrypted `SecureString`
   * SSM parameter under `--from-cfn-stack` (issue #99). Unioned with
   * {@link SENSITIVE_ENV_KEYS} before {@link appendEnvFlags}.
   */
  sensitiveEnvKeys?: ReadonlySet<string>;
  /**
   * Container CMD. For ZIP Lambda base images this is the handler string,
   * e.g. `index.handler`. For container Lambdas (PR 5) this is the
   * `ImageConfig.Command` array — passed verbatim, may be empty when the
   * image's own CMD is sufficient.
   */
  cmd: string[];
  /** Host port to bind the container port ({@link containerPort}) to. */
  hostPort: number;
  /**
   * Container port to publish {@link hostPort} to. Defaults to `8080` (the
   * Lambda RIE port + the AgentCore HTTP contract port). `cdkl
   * invoke-agentcore` overrides it to `8000` for an MCP-protocol runtime,
   * whose container serves `POST /mcp` on `0.0.0.0:8000`.
   */
  containerPort?: number;
  /** Host to bind to (default `127.0.0.1`). */
  host?: string;
  /**
   * Optional Node.js inspector port. When set the container also publishes
   * `<port>:<port>` and the caller is expected to have set
   * `NODE_OPTIONS=--inspect-brk=0.0.0.0:<port>` in `env`.
   */
  debugPort?: number;
  /**
   * `--platform <linux/amd64|linux/arm64>`. PR 5: container Lambdas
   * declare `Architectures`, and the run-time platform must match the
   * built image. Without this an arm64 host running an x86_64 Lambda
   * hits emulation (slow) and an x86_64 host running arm64 fails with
   * `exec format error`. Omitted when undefined (the ZIP path on the
   * host's default arch is the original behavior).
   */
  platform?: string;
  /**
   * `--entrypoint <first>` for container Lambdas (PR 5,
   * `ImageConfig.EntryPoint`). When set, only the first entry is passed
   * to docker as `--entrypoint` (docker accepts a single binary there);
   * the remaining entries are pre-pended to `cmd` as positional args.
   * Most container Lambdas leave EntryPoint unset so `/lambda-entrypoint.sh`
   * stays in charge of dispatching to RIE.
   */
  entryPoint?: string[];
  /** `--workdir <dir>` for container Lambdas (PR 5, `ImageConfig.WorkingDirectory`). */
  workingDir?: string;
  /**
   * Optional `--name` for the container. `cdkl start-api` sets a
   * stable `cdkl-<logicalId>-<pid>-<rand>` name so the verify.sh
   * trap can sweep orphans (`docker ps --filter name=cdkl-`)
   * regardless of how the server exited. `cdkl invoke` leaves it
   * unset and lets docker auto-assign — short-lived containers don't
   * benefit from a stable name.
   */
  name?: string;
  /**
   * Optional `--network <name>` for the container. `cdkl run-task`
   * uses this so every container in a task lands on the same per-task
   * docker network and can reach the metadata-endpoints sidecar at
   * `169.254.170.2`. Unset → docker's default bridge network (current
   * behavior for `cdkl invoke` / `cdkl start-api`).
   */
  network?: string;
  /**
   * Optional sized tmpfs mount (issue #440 — Lambda
   * `Properties.EphemeralStorage.Size`). When set, emits
   * `--tmpfs <target>:rw,size=<sizeMb>m` so the container's `<target>`
   * (typically `/tmp`) is a memory-backed filesystem capped at
   * `sizeMb` MiB. Handlers that exceed the cap fail with `ENOSPC`
   * the way they would on AWS, and handlers that detect free space
   * via `statvfs` / `df` see the configured cap rather than the
   * host's overlay-fs.
   *
   * Unset → no `--tmpfs` flag, the container's `/tmp` is whatever
   * the base image provides (preserves the pre-#440 behavior for
   * Lambdas without `EphemeralStorage`).
   */
  tmpfs?: { target: string; sizeMb: number };
  /**
   * Extra `--add-host <host>:<ip>` mappings written into the
   * container's `/etc/hosts`. Used by `cdkl start-api`'s
   * WebSocket support (#462) to add
   * `host.docker.internal:host-gateway` so the Lambda container can
   * reach the host's `@connections` HTTP endpoint regardless of OS
   * (Docker Desktop on macOS / Windows already resolves
   * `host.docker.internal`; Linux native dockerd needs the explicit
   * `host-gateway` flag since 20.10+).
   */
  extraHosts?: { host: string; ip: string }[];
}

/**
 * Pull the image. No-op when `skipPull` is true.
 *
 * In verbose mode (`--verbose` / global log level `debug`), streams the
 * full `docker pull` progress to stdout so the user sees per-layer
 * downloads. In the default compact mode the call is silent (cached
 * images are the common case; a fresh pull still shows progress only
 * via `--verbose`). Errors are always surfaced: the captured stderr is
 * folded into the thrown `DockerRunnerError` message.
 */
export async function pullImage(
  image: string,
  skipPull: boolean,
  platform?: string
): Promise<void> {
  const logger = getLogger().child('docker');
  if (skipPull) {
    logger.debug(`Skipping docker pull for ${image} (--no-pull)`);
    return;
  }
  // Pull the variant matching the Lambda's declared `Architectures`. Without
  // `--platform` docker picks the host's native arch, so on an arm64 host a
  // function declared `x86_64` (or vice versa) gets a mismatched base image and
  // a compiled `provided.*` `bootstrap` fails to exec ("exec format error").
  const platformArgs = platform ? ['--platform', platform] : [];
  if (getLogger().getLevel() === 'debug') {
    logger.info(`Pulling ${image}${platform ? ` (${platform})` : ''}...`);
    try {
      await runDockerForeground(['pull', ...platformArgs, image]);
    } catch (err) {
      const e = err as Error;
      throw new DockerRunnerError(`docker pull ${image} failed: ${e.message}`);
    }
    return;
  }
  logger.debug(`Pulling ${image} (silent — pass --verbose to stream progress)`);
  // Captured-mode pull: stdout/stderr collected, mirrored only when
  // --verbose is on (which the branch above already handles via
  // runDockerForeground), so streamLive: false keeps the silent
  // contract on the default compact log level. The spinner shows
  // motion during what is otherwise a multi-minute silent pull
  // against a real-world image.
  try {
    await runDockerStreaming(['pull', ...platformArgs, image], {
      streamLive: false,
      progressLabel: `Pulling ${image}`,
    });
  } catch (err) {
    const e = err as {
      exitCode?: number | null;
      stderr?: string;
      stdout?: string;
      message?: string;
    };
    // Distinguish spawn-level failure (ENOENT — docker binary missing,
    // surfaces with the helpful Install-Docker / CDK_DOCKER hint from
    // `spawnStreaming`'s ENOENT branch) from non-zero exit (genuine
    // pull failure with captured stderr). The ENOENT path rejects with
    // a plain `Error` (no `exitCode` field); the non-zero-exit path
    // rejects with `SpawnError` (`exitCode` + `stderr` + `stdout`).
    if (e.exitCode === undefined || e.exitCode === null) {
      throw new DockerRunnerError(`docker pull ${image} failed: ${e.message ?? String(err)}`);
    }
    const detail = e.stderr?.trim() || e.stdout?.trim() || '(no output)';
    throw new DockerRunnerError(`docker pull ${image} exited with code ${e.exitCode}: ${detail}`);
  }
}

/**
 * Run the container detached. Returns the container ID.
 *
 * The caller is responsible for:
 *   - polling `host:port` for RIE readiness,
 *   - issuing the invoke,
 *   - calling `removeContainer` from a `try`/`finally` so the container
 *     is cleaned up on any error path including SIGINT.
 */
export async function runDetached(opts: DockerRunOptions): Promise<string> {
  const args: string[] = ['run', '-d', '--rm'];

  if (opts.name) {
    args.push('--name', opts.name);
  }
  if (opts.network) {
    args.push('--network', opts.network);
  }
  if (opts.platform) {
    args.push('--platform', opts.platform);
    // Surface CPU emulation (declared arch != host arch) before the
    // container boots — an emulated compiled custom runtime can die with
    // an opaque `exec format error` / `illegal instruction` otherwise.
    warnIfEmulatedPlatform(opts.platform, opts.name ? { label: opts.name } : {});
  }
  if (opts.extraHosts) {
    for (const entry of opts.extraHosts) {
      args.push('--add-host', `${entry.host}:${entry.ip}`);
    }
  }

  const host = opts.host ?? '127.0.0.1';
  args.push('-p', `${host}:${opts.hostPort}:${opts.containerPort ?? 8080}`);
  if (opts.debugPort !== undefined) {
    args.push('-p', `${host}:${opts.debugPort}:${opts.debugPort}`);
  }

  for (const mount of opts.mounts) {
    const ro = mount.readOnly ? ':ro' : '';
    args.push('-v', `${mount.hostPath}:${mount.containerPath}${ro}`);
  }
  // PR 6 (#232): layer mounts are emitted after the function's own
  // mounts. Order within `extraMounts` is preserved — the caller (the
  // CLI's `resolveZipImagePlan`) feeds layers in the same order they
  // appear in `Properties.Layers`, so AWS's "last layer wins on file
  // collision" semantics hold.
  if (opts.extraMounts) {
    for (const mount of opts.extraMounts) {
      const ro = mount.readOnly ? ':ro' : '';
      args.push('-v', `${mount.hostPath}:${mount.containerPath}${ro}`);
    }
  }

  // AWS credentials (and any caller-flagged sensitive keys, e.g. decrypted
  // SecureString SSM values — issue #99) route through docker's
  // value-from-process-env form (`-e KEY`, value supplied via the spawned
  // process's env below) so they never appear in `docker run`'s argv.
  // Non-sensitive env keeps the inline `-e KEY=VALUE` form.
  const sensitiveKeys =
    opts.sensitiveEnvKeys && opts.sensitiveEnvKeys.size > 0
      ? new Set<string>([...SENSITIVE_ENV_KEYS, ...opts.sensitiveEnvKeys])
      : SENSITIVE_ENV_KEYS;
  const passthroughEnv = appendEnvFlags(args, opts.env, sensitiveKeys);

  // Issue #440 — Lambda EphemeralStorage.Size: emit `--tmpfs
  // <target>:rw,size=<N>m` so the container's `/tmp` is capped at the
  // templated value. Placed AFTER -e flags and BEFORE --workdir to
  // keep the order stable with the existing `-p` / `-v` / `-e`
  // mount-shaped block; downstream `--workdir` / `--entrypoint`
  // / image / cmd ordering is unchanged.
  if (opts.tmpfs) {
    args.push('--tmpfs', `${opts.tmpfs.target}:rw,size=${opts.tmpfs.sizeMb}m`);
  }

  if (opts.workingDir) {
    args.push('--workdir', opts.workingDir);
  }

  // ImageConfig.EntryPoint maps to `--entrypoint <first>` plus the rest
  // as positional args before CMD. Docker only accepts a single value
  // for `--entrypoint`; multi-arg entrypoints carry their tail through
  // the positional CMD slot. Most container Lambdas omit EntryPoint
  // entirely so `/lambda-entrypoint.sh` stays in charge of RIE dispatch.
  let entryPointTail: string[] = [];
  if (opts.entryPoint && opts.entryPoint.length > 0) {
    args.push('--entrypoint', opts.entryPoint[0]!);
    entryPointTail = opts.entryPoint.slice(1);
  }

  args.push(opts.image, ...entryPointTail, ...opts.cmd);

  const logger = getLogger().child('docker');
  logger.debug(`${getDockerCmd()} ${redactAwsCredentialsInArgs(args).join(' ')}`);

  try {
    const { stdout } = await execFileAsync(getDockerCmd(), args, {
      maxBuffer: 10 * 1024 * 1024,
      ...execEnvForSecrets(passthroughEnv),
    });
    return stdout.trim();
  } catch (error) {
    const err = error as { stderr?: string; message?: string };
    throw new DockerRunnerError(
      `docker run failed: ${err.stderr?.trim() || err.message || String(error)}`
    );
  }
}

/**
 * `docker logs -f <id>` plumbed to stdout/stderr. Returns a function that
 * stops the stream (used by the caller in a `finally` block).
 */
export function streamLogs(containerId: string): () => void {
  const proc = spawn(getDockerCmd(), ['logs', '-f', containerId], {
    stdio: ['ignore', 'pipe', 'pipe'],
  });
  proc.stdout?.on('data', (chunk: Buffer) => process.stdout.write(chunk));
  proc.stderr?.on('data', (chunk: Buffer) => process.stderr.write(chunk));
  // Swallow the exit code; this child is just plumbing.
  proc.on('error', () => {
    /* the parent flow surfaces docker errors via runDetached / removeContainer */
  });
  return () => {
    if (!proc.killed) proc.kill('SIGTERM');
  };
}

/**
 * Best-effort `docker rm -f <id>`. Errors are swallowed (logged at debug)
 * because this typically runs from a `finally` and the parent has its own
 * error to surface.
 */
export async function removeContainer(containerId: string): Promise<void> {
  if (!containerId) return;
  const logger = getLogger().child('docker');
  try {
    await execFileAsync(getDockerCmd(), ['rm', '-f', containerId]);
    logger.debug(`Removed container ${containerId}`);
  } catch (error) {
    const err = error as { stderr?: string; message?: string };
    logger.debug(
      `docker rm -f ${containerId} failed: ${err.stderr || err.message || String(error)}`
    );
  }
}

/**
 * Verify the docker daemon is reachable. Surfaces a friendlier error than
 * the raw `ENOENT` / "Cannot connect to the Docker daemon" the user would
 * otherwise see at the first run call. Called once up front.
 */
export async function ensureDockerAvailable(): Promise<void> {
  const cmd = getDockerCmd();
  try {
    await execFileAsync(cmd, ['version', '--format', '{{.Server.Version}}']);
  } catch (error) {
    const err = error as { code?: string; stderr?: string; message?: string };
    if (err.code === 'ENOENT') {
      throw new DockerRunnerError(
        `${cmd} is not installed or not on PATH. ${getEmbedConfig().cliName} invoke needs Docker (or a compatible CLI specified via CDK_DOCKER) — install it and retry.`
      );
    }
    throw new DockerRunnerError(
      `${cmd} daemon is not reachable: ${err.stderr?.trim() || err.message || String(error)}. ` +
        'Start Docker Desktop / the docker daemon and retry.'
    );
  }
}

/**
 * Allocate a free TCP port on `127.0.0.1`. Used to pick a host port for
 * publishing the RIE :8080 endpoint without colliding with whatever else
 * the user has running. The OS assigns a port via `port: 0` and we
 * close the probe before returning so docker can bind it next.
 *
 * There is a tiny race window between close and `docker run -p` — in
 * practice it's never been observed for local invoke; if it ever
 * surfaces, the caller can retry with a fresh port.
 */
export function pickFreePort(): Promise<number> {
  return new Promise<number>((resolvePort, rejectPort) => {
    const server = createServer();
    server.unref();
    server.on('error', rejectPort);
    server.listen(0, '127.0.0.1', () => {
      const address = server.address();
      if (!address || typeof address === 'string') {
        server.close();
        rejectPort(new Error('Could not allocate a host port'));
        return;
      }
      const port = address.port;
      server.close(() => resolvePort(port));
    });
  });
}

/**
 * Env keys whose VALUES are sensitive (AWS credentials). These are
 * routed through docker's value-from-process-env form by
 * {@link appendEnvFlags} so the value never appears in `docker run`'s
 * argv (`ps` / `/proc/<pid>/cmdline`), and they are also redacted by
 * {@link redactAwsCredentialsInArgs} as a defense for any path that
 * still emits the inline `-e <KEY>=<value>` form into the debug log.
 */
export const SENSITIVE_ENV_KEYS: ReadonlySet<string> = new Set([
  'AWS_ACCESS_KEY_ID',
  'AWS_SECRET_ACCESS_KEY',
  'AWS_SESSION_TOKEN',
]);

/**
 * Append `-e` flags for `env` to `args`, routing keys in `sensitiveKeys`
 * through docker's value-from-process-env form (`-e KEY`, with NO
 * `=value`). Docker reads the value from its OWN environment at run time,
 * so the secret never lands in `docker run`'s argv — invisible to
 * `ps aux` / `/proc/<pid>/cmdline` (other users on a shared host) and to
 * shell history. Non-sensitive keys keep the inline `-e KEY=VALUE` form
 * (fine for non-secret config, and keeps `--debug` output readable).
 *
 * Returns the `{ KEY: value }` map of routed-through keys; the caller MUST
 * merge it into the spawned docker process's `env` (see
 * {@link execEnvForSecrets}) so docker can resolve each passed-through
 * key. Values still appear in `docker inspect` Config.Env — that is
 * inherent to container env and matches production behavior.
 *
 * Unlike `--env-file`, this handles multi-line values (e.g. PEM secrets)
 * and needs no temp file on disk.
 */
export function appendEnvFlags(
  args: string[],
  env: Record<string, string>,
  sensitiveKeys: ReadonlySet<string>
): Record<string, string> {
  const passthrough: Record<string, string> = {};
  for (const [k, v] of Object.entries(env)) {
    if (sensitiveKeys.has(k)) {
      args.push('-e', k);
      passthrough[k] = v;
    } else {
      args.push('-e', `${k}=${v}`);
    }
  }
  return passthrough;
}

/**
 * Build the `env` option for an `execFile` / `spawn` call that runs a
 * `docker run` whose args include passed-through sensitive keys (from
 * {@link appendEnvFlags}). Returns `{ env: {...process.env, ...passthrough} }`
 * so docker inherits the normal environment PLUS the sensitive values it
 * must resolve, or `{}` when there is nothing to pass through (preserving
 * the default inherited-environment behavior).
 */
export function execEnvForSecrets(passthrough: Record<string, string>): {
  env?: NodeJS.ProcessEnv;
} {
  if (Object.keys(passthrough).length === 0) return {};
  return { env: { ...process.env, ...passthrough } };
}

/**
 * Returns a copy of `args` with any `-e <KEY>=<value>` pair whose KEY is
 * in {@link SENSITIVE_ENV_KEYS} replaced with `-e <KEY>=***`. The actual
 * `args` passed to `spawn` are never mutated — this is for log output
 * only. With {@link appendEnvFlags} routing credentials through the
 * value-less `-e KEY` form this rarely fires, but it stays as defense for
 * any inline-form credential that slips into a logged command.
 */
export function redactAwsCredentialsInArgs(args: readonly string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < args.length; i++) {
    const cur = args[i]!;
    const next = args[i + 1];
    if (cur === '-e' && typeof next === 'string') {
      const eqIdx = next.indexOf('=');
      if (eqIdx > 0) {
        const key = next.substring(0, eqIdx);
        if (SENSITIVE_ENV_KEYS.has(key)) {
          out.push('-e', `${key}=***`);
          i++;
          continue;
        }
      }
    }
    out.push(cur);
  }
  return out;
}
